<template>
  <section class="issues-tab" data-test="issues-tab">
    <header class="filters" role="toolbar" aria-label="Issues filters">
      <button
        v-for="b in buckets"
        :key="b.id"
        type="button"
        class="bucket"
        :class="{ active: bucket === b.id }"
        :data-test="`bucket-${b.id}`"
        @click="bucket = b.id"
      >
        <span>{{ b.label }}</span>
        <span class="count mono">{{ b.count }}</span>
      </button>
      <button
        v-if="complianceFlaggedCount > 0"
        type="button"
        class="bucket flagged"
        :class="{ active: bucket === 'flagged' }"
        data-test="bucket-flagged"
        @click="bucket = 'flagged'"
      >
        <span>Hygiene flagged</span>
        <span class="count mono">{{ complianceFlaggedCount }}</span>
      </button>
    </header>

    <div v-if="filtered.length" class="table-wrap">
      <table class="issues">
        <thead>
          <tr>
            <th>Key</th>
            <th>Type</th>
            <th>Status</th>
            <th>Age</th>
            <th>SP</th>
            <th>Priority</th>
            <th>Summary</th>
            <th>Hygiene</th>
            <th>Assignee</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="issue in filtered"
            :key="issue.key"
            :class="rowClasses(issue)"
            :data-test="`issue-${issue.key}`"
          >
            <td class="mono key-cell">
              <a :href="jiraUrl(issue.key)" target="_blank" rel="noopener noreferrer">{{ issue.key }}</a>
              <span v-if="issue.added_after_start" class="pill add" title="Added after sprint start">+</span>
              <span v-if="issue.is_stuck" class="pill stuck" title="Stuck > 3 days">!</span>
            </td>
            <td class="type">{{ issue.type }}</td>
            <td>
              <span class="status-chip" :class="statusBucket(issue)">{{ issue.status }}</span>
            </td>
            <td class="mono age">
              <template v-if="issue.days_in_status != null">
                <span :title="ageTooltip(issue)">{{ issue.days_in_status }}d<span
                  v-if="showSprintIdle(issue)"
                  class="age-sub"
                > · {{ issue.days_idle_in_sprint }}d sprint</span></span>
              </template>
              <template v-else>—</template>
            </td>
            <td class="mono sp">{{ issue.story_points ?? "—" }}</td>
            <td class="priority">{{ issue.priority ?? "—" }}</td>
            <td class="summary" :title="issue.summary">{{ issue.summary }}</td>
            <td class="hygiene-cell">
              <a
                v-for="v in violationsFor(issue.key)"
                :key="v.rule_id"
                :href="pillUrl(v.rule_id)"
                target="_blank"
                rel="noopener noreferrer"
                class="hygiene-pill"
                :title="`${v.why} — click to open all ${v.rule_id} tickets in Jira`"
                :data-test="`hygiene-pill-${issue.key}-${v.rule_id}`"
              >{{ v.rule_id }}</a>
              <template v-if="violationsFor(issue.key).length === 0">—</template>
            </td>
            <td class="assignee">{{ issue.assignee ?? "—" }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else class="empty">No tickets in this bucket.</p>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { ComplianceViolation, IssueSummary, SprintDetail } from "@/api/types";
import { buildIssueUrl, ruleFilterUrl } from "@/lib/jiraJql";

const props = defineProps<{ sprint: SprintDetail }>();

type BucketId = "all" | "stuck" | "open" | "in_flight" | "delivered" | "added" | "flagged";
const bucket = ref<BucketId>("all");

const all = computed<IssueSummary[]>(() => props.sprint.metrics?.issues_summary ?? []);

// Compliance audit lives at sprint.compliance (from tag_compliance step).
// We index by ticket key so per-row pills are O(1).
const complianceByKey = computed<Record<string, ComplianceViolation[]>>(() => {
  const actions = props.sprint.compliance?.actions ?? [];
  const out: Record<string, ComplianceViolation[]> = {};
  for (const a of actions) {
    if (a.violations && a.violations.length) {
      out[a.key] = a.violations;
    }
  }
  return out;
});

function violationsFor(key: string): ComplianceViolation[] {
  return complianceByKey.value[key] ?? [];
}

const complianceFlaggedCount = computed(() => Object.keys(complianceByKey.value).length);

const TERMINAL = new Set([
  "Done", "Closed", "Resolved",
  "Closed Fixed", "Closed as designed",
  "Released", "Ready for Releasing", "Ready for Release",
]);
const OPEN = new Set(["Open", "Backlog", "To Do", "Reopened"]);

function isOpen(i: IssueSummary): boolean {
  return OPEN.has(i.status);
}
function isInFlight(i: IssueSummary): boolean {
  return !i.is_delivered && !isOpen(i);
}
function statusBucket(i: IssueSummary): "done" | "open" | "in-flight" {
  if (i.is_delivered) return "done";
  if (isOpen(i)) return "open";
  return "in-flight";
}

const buckets = computed(() => [
  { id: "all" as const, label: "All", count: all.value.length },
  { id: "stuck" as const, label: "Stuck", count: all.value.filter((i) => i.is_stuck).length },
  { id: "open" as const, label: "Open", count: all.value.filter(isOpen).length },
  { id: "in_flight" as const, label: "In flight", count: all.value.filter(isInFlight).length },
  { id: "delivered" as const, label: "Delivered", count: all.value.filter((i) => i.is_delivered).length },
  { id: "added" as const, label: "Added mid-sprint", count: all.value.filter((i) => i.added_after_start).length },
]);

const filtered = computed<IssueSummary[]>(() => {
  const list = all.value;
  switch (bucket.value) {
    case "stuck": return list.filter((i) => i.is_stuck);
    case "open": return list.filter(isOpen);
    case "in_flight": return list.filter(isInFlight);
    case "delivered": return list.filter((i) => i.is_delivered);
    case "added": return list.filter((i) => i.added_after_start);
    case "flagged": return list.filter((i) => violationsFor(i.key).length > 0);
    default: return list;
  }
});

function rowClasses(issue: IssueSummary): Record<string, boolean> {
  return {
    stuck: issue.is_stuck,
    delivered: issue.is_delivered,
  };
}

// Centralised in src/lib/jiraJql.ts; re-exported here for template use.
const jiraUrl = buildIssueUrl;
const projectKey = computed(() => props.sprint.project_key);
function pillUrl(ruleId: string): string {
  return ruleFilterUrl(projectKey.value, ruleId);
}

// Two-clock display: `days_in_status` is the absolute Jira age, while
// `days_idle_in_sprint` is the actionable "how long has this been idle
// during the sprint window" number. Show the sprint clock as a suffix
// only when (a) it's defined and (b) it differs from the absolute clock
// — otherwise we'd just clutter every row with a redundant "5d · 5d sprint".
function showSprintIdle(i: IssueSummary): boolean {
  const idle = i.days_idle_in_sprint;
  return idle != null && idle !== i.days_in_status;
}
function ageTooltip(i: IssueSummary): string {
  const total = `${i.days_in_status}d since last status change`;
  if (i.days_idle_in_sprint == null) return total;
  return `${total} · ${i.days_idle_in_sprint}d idle inside this sprint`;
}
</script>

<style scoped>
.issues-tab { display: flex; flex-direction: column; gap: 12px; padding-top: 8px; }

.filters { display: flex; flex-wrap: wrap; gap: 6px; }
.bucket {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--surface-card, #fff); border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 999px; padding: 4px 12px; font-size: 12px; cursor: pointer;
  color: var(--text-color, #111827);
}
.bucket:hover { background: var(--surface-elev, #f3f4f6); }
.bucket.active { background: #2563eb; border-color: #1d4ed8; color: #fff; }
.bucket.flagged { border-color: #fca5a5; color: #b91c1c; }
.bucket.flagged.active { background: #dc2626; border-color: #b91c1c; color: #fff; }
.bucket .count { font-size: 11px; opacity: 0.85; }

.hygiene-cell { white-space: nowrap; max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
.hygiene-pill {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  margin-right: 4px;
  text-transform: lowercase;
  letter-spacing: 0.02em;
  text-decoration: none;
  cursor: pointer;
}
.hygiene-pill:hover { background: #fecaca; text-decoration: underline; }

.table-wrap { overflow-x: auto; border: 1px solid var(--surface-border, #e5e7eb); border-radius: 8px; background: var(--surface-card, #fff); }
table.issues { border-collapse: collapse; width: 100%; font-size: 12px; }
table.issues thead th {
  text-align: left; padding: 8px 10px; background: var(--surface-elev, #f9fafb);
  font-weight: 600; color: var(--text-muted, #4b5563);
  text-transform: uppercase; letter-spacing: 0.04em; font-size: 10px;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
}
table.issues tbody td { padding: 8px 10px; border-bottom: 1px solid var(--surface-border, #f3f4f6); vertical-align: top; }
tr.stuck { background: #fffbeb; }
tr.delivered { color: var(--text-muted, #6b7280); }

.key-cell { white-space: nowrap; }
.key-cell a { color: #2563eb; text-decoration: none; font-weight: 600; }
.key-cell a:hover { text-decoration: underline; }
.pill {
  display: inline-block; min-width: 14px; text-align: center;
  font-size: 10px; line-height: 14px; border-radius: 4px;
  margin-left: 4px; font-weight: 600;
}
.pill.add { background: #dbeafe; color: #1d4ed8; }
.pill.stuck { background: #fef3c7; color: #b45309; }

.type { color: var(--text-muted, #6b7280); }
.status-chip {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px;
  background: var(--surface-elev, #f3f4f6); color: var(--text-color, #111827);
}
.status-chip.done { background: #dcfce7; color: #166534; }
.status-chip.open { background: #f3f4f6; color: #4b5563; }
.status-chip.in-flight { background: #dbeafe; color: #1e40af; }

.age, .sp { white-space: nowrap; color: var(--text-muted, #4b5563); }
.age-sub { color: var(--text-muted, #9ca3af); font-size: 11px; margin-left: 2px; }
.summary { max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.priority, .assignee { color: var(--text-muted, #6b7280); white-space: nowrap; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.empty { font-size: 13px; color: var(--text-muted, #6b7280); padding: 24px; text-align: center; }
</style>
